import React, { useState } from "react";
import { Heart, Check, Shield, Repeat, Gift, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { useToast } from "../hooks/use-toast";
import Reveal from "../components/Reveal";
import { donationTiers, heroImages } from "../mock";

const Donate = () => {
  const { toast } = useToast();
  const [freq, setFreq] = useState("once");
  const [amount, setAmount] = useState(50);
  const [custom, setCustom] = useState("");

  const chosen = custom ? Number(custom) : amount;
  const selectedTier = donationTiers.find((t) => t.amount === amount);

  const donate = (e) => {
    e.preventDefault();
    toast({
      title: "Thank you for your generosity! 💚",
      description: `Your ${freq === "monthly" ? "monthly" : "one-time"} gift of $${chosen || 0} means the world. (Demo — no payment processed.)`,
    });
  };

  return (
    <div>
      <section className="pt-32 lg:pt-40 pb-16 bg-secondary/40 relative overflow-hidden">
        <div className="absolute -top-20 right-10 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
        <div className="container-prc relative text-center max-w-3xl mx-auto">
          <Badge className="bg-white text-primary hover:bg-white rounded-full px-4 py-1.5 font-semibold mb-5">Donate</Badge>
          <h1 className="font-display font-extrabold text-4xl lg:text-5xl text-foreground text-balance">Your gift keeps families connected</h1>
          <p className="mt-5 text-lg text-muted-foreground">Every dollar directly supports scholarships, classes, and support circles for local families.</p>
        </div>
      </section>

      <section className="py-16">
        <div className="container-prc grid lg:grid-cols-5 gap-10 items-start">
          <Reveal className="lg:col-span-3">
            <form onSubmit={donate} className="bg-white rounded-[1.75rem] p-7 lg:p-9 shadow-lift">
              <div className="grid grid-cols-2 gap-2 p-1.5 bg-secondary rounded-2xl mb-8">
                {[{ k: "once", label: "One-time" }, { k: "monthly", label: "Monthly" }].map((o) => (
                  <button
                    type="button"
                    key={o.k}
                    onClick={() => setFreq(o.k)}
                    className={`h-11 rounded-xl font-bold text-sm transition-colors flex items-center justify-center gap-2 ${
                      freq === o.k ? "bg-primary text-primary-foreground shadow" : "text-foreground/60"
                    }`}
                  >
                    {o.k === "monthly" && <Repeat className="h-4 w-4" />} {o.label}
                  </button>
                ))}
              </div>

              <p className="font-display font-bold text-foreground mb-3">Choose an amount</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {donationTiers.map((t) => (
                  <button
                    type="button"
                    key={t.amount}
                    onClick={() => { setAmount(t.amount); setCustom(""); }}
                    className={`h-16 rounded-xl border-2 font-display font-extrabold text-lg transition-all ${
                      amount === t.amount && !custom ? "border-primary bg-primary/5 text-primary" : "border-border text-foreground/70 hover:border-primary/40"
                    }`}
                  >
                    ${t.amount}
                  </button>
                ))}
              </div>

              <div className="mt-4 relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">$</span>
                <Input
                  type="number"
                  min="1"
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  placeholder="Enter custom amount"
                  className="h-14 pl-8 rounded-xl text-lg font-bold"
                />
              </div>

              {selectedTier && !custom && (
                <p className="mt-4 flex items-start gap-2 text-sm text-primary bg-primary/5 rounded-xl p-3">
                  <Gift className="h-5 w-5 shrink-0" /> {selectedTier.label}
                </p>
              )}

              <div className="mt-7 border-t border-border pt-7 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input required placeholder="First name" className="h-11 rounded-xl" />
                  <Input required placeholder="Last name" className="h-11 rounded-xl" />
                </div>
                <Input required type="email" placeholder="Email address" className="h-11 rounded-xl" />
                <Input placeholder="Card number" className="h-11 rounded-xl" />
                <div className="grid grid-cols-2 gap-4">
                  <Input placeholder="MM / YY" className="h-11 rounded-xl" />
                  <Input placeholder="CVC" className="h-11 rounded-xl" />
                </div>
              </div>

              <Button type="submit" className="btn-cta w-full mt-7 bg-accent hover:bg-accent/90 text-accent-foreground font-bold rounded-full h-13 py-3.5 text-base">
                <Heart className="h-5 w-5 mr-2" /> Give ${chosen || 0}{freq === "monthly" ? "/month" : ""}
              </Button>
              <p className="mt-3 text-xs text-muted-foreground text-center flex items-center justify-center gap-1.5">
                <Shield className="h-3.5 w-3.5" /> Secure & tax-deductible • Demo checkout, no real charge
              </p>
            </form>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-2 space-y-6">
            <div className="img-zoom rounded-[1.75rem] overflow-hidden shadow-soft">
              <img src={heroImages.donate} alt="Your impact" className="w-full h-56 object-cover" />
            </div>
            <div className="bg-white rounded-[1.75rem] p-7 shadow-soft">
              <h3 className="font-display font-bold text-xl text-foreground">Where your gift goes</h3>
              <ul className="mt-4 space-y-3">
                {[
                  "Scholarships so no family is turned away",
                  "Free parent support circles",
                  "Classroom supplies & materials",
                  "Community events year-round",
                ].map((t) => (
                  <li key={t} className="flex items-start gap-3 text-sm text-foreground/90">
                    <span className="grid place-items-center h-5 w-5 rounded-full bg-primary/10 text-primary shrink-0 mt-0.5"><Check className="h-3 w-3" /></span>
                    {t}
                  </li>
                ))}
              </ul>
              <div className="mt-6 flex items-center gap-3 text-sm text-muted-foreground bg-secondary rounded-xl p-3">
                <CreditCard className="h-5 w-5 text-primary" /> Prefer to mail a check? Contact our office for details.
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Donate;
